import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { Button, DatePicker, Input, formatDate } from '@bnvsrnd/brand-assets';
import api from '../../utils/api';
import { trackEvent } from '../../utils/visitorTracker';
import './AdmissionInquiryModal.css';

/**
 * 도입 문의 모달 (메인 온보딩 헤더 "도입 문의" 버튼)
 * - 선생님 성함 / 연락처 / 메일주소 / 수업 기간(장기·단기·미정 + 확정 날짜) / 관심 커리큘럼(중복선택)
 * - 전송 → POST /api/leads/public (공개) → "빠른 시일 내 연락드리겠습니다." 안내
 * props: { onClose }
 */

const TERMS = ['장기', '단기', '미정'];

const CURRICULA = [
    'AI 크리에이터',
    '스타트업 메이커스',
    '바이브코딩',
    '인공지능+빅데이터',
    '필기체 숫자 인식',
    '음성 데이터분석',
    '천체 이미지 데이터',
    '혈액 데이터',
    '교직원 연수',
];

function AdmissionInquiryModal({ onClose }) {
    const [name, setName] = useState('');
    const [contact, setContact] = useState('');
    const [email, setEmail] = useState('');
    const [term, setTerm] = useState('');       // 장기 | 단기 | 미정 | ''
    const [date, setDate] = useState(null);      // 확정 날짜(선택, Date)
    const [curricula, setCurricula] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const toggleCurriculum = (c) =>
        setCurricula((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

    const handleSubmit = async () => {
        if (!name.trim()) { setError('선생님 성함을 입력해주세요.'); return; }
        if (!contact.trim()) { setError('연락처를 입력해주세요.'); return; }
        setError('');
        setSubmitting(true);
        try {
            // 관리자 리드 관리에서 한눈에 보이도록 일정/커리큘럼을 message 로 요약
            const parts = [];
            if (term) parts.push(`수업 기간: ${term}`);
            if (date) parts.push(`확정 날짜: ${formatDate(date)}`);
            if (curricula.length) parts.push(`관심 커리큘럼: ${curricula.join(', ')}`);
            const message = parts.join('\n');

            await api.post('/leads/public', {
                name: name.trim(),
                contact: contact.trim(),
                email: email.trim(),
                message,
                termType: term === '장기' || term === '단기' ? term : undefined,
                desiredCurriculum: curricula.join(', '),
                source: '도입문의',
            });
            trackEvent('inquiry_submit', { kind: 'admission', curricula: curricula.join(', ').slice(0, 200), term: term || '' });
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.message || '전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="aiq-overlay" onClick={onClose}>
            <div className="aiq-card" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="aiq-close" aria-label="닫기" onClick={onClose}>
                    <X size={20} />
                </button>

                {done ? (
                    <div className="aiq-done">
                        <span className="aiq-done-icon"><Check size={28} /></span>
                        <h3 className="aiq-done-title">문의가 접수되었습니다</h3>
                        <p className="aiq-done-desc">빠른 시일 내 연락드리겠습니다.</p>
                        <Button size="lg" className="aiq-submit" onClick={onClose}>확인</Button>
                    </div>
                ) : (
                    <>
                        <div className="aiq-head">
                            <h3 className="aiq-title">도입 문의</h3>
                            <p className="aiq-sub">학교/기관 도입을 검토 중이신가요? 아래 정보를 남겨주시면 담당자가 연락드립니다.</p>
                        </div>

                        <div className="aiq-body">
                            <div className="aiq-field">
                                <label className="aiq-label" htmlFor="aiq-name">선생님 성함 <span className="aiq-req">*</span></label>
                                <Input id="aiq-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="예) 홍길동" />
                            </div>

                            <div className="aiq-row">
                                <div className="aiq-field">
                                    <label className="aiq-label" htmlFor="aiq-contact">연락처 <span className="aiq-req">*</span></label>
                                    <Input id="aiq-contact" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="예) 010-1234-5678" />
                                </div>
                                <div className="aiq-field">
                                    <label className="aiq-label" htmlFor="aiq-email">메일주소</label>
                                    <Input id="aiq-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="예) teacher@school.kr" />
                                </div>
                            </div>

                            <div className="aiq-field">
                                <label className="aiq-label">수업 기간</label>
                                <div className="aiq-terms">
                                    {TERMS.map((t) => (
                                        <Button
                                            key={t}
                                            variant={term === t ? 'primary' : 'secondary'}
                                            tone="soft"
                                            shape="pill"
                                            aria-pressed={term === t}
                                            onClick={() => setTerm((prev) => (prev === t ? '' : t))}
                                        >
                                            {t}
                                        </Button>
                                    ))}
                                    <DatePicker
                                        className="aiq-date"
                                        value={date}
                                        onChange={setDate}
                                        format="YYYY. MM. DD."
                                        placeholder="연도. 월. 일."
                                        aria-label="확정 날짜"
                                    />
                                </div>
                                <p className="aiq-hint">날짜가 정해졌다면 확정 날짜를 함께 선택해주세요.</p>
                            </div>

                            <div className="aiq-field">
                                <label className="aiq-label">관심 커리큘럼 <span className="aiq-multi">(중복 선택 가능)</span></label>
                                <div className="aiq-curricula">
                                    {CURRICULA.map((c) => (
                                        <Button
                                            key={c}
                                            variant={curricula.includes(c) ? 'primary' : 'secondary'}
                                            tone="soft"
                                            shape="pill"
                                            className="aiq-chip"
                                            aria-pressed={curricula.includes(c)}
                                            onClick={() => toggleCurriculum(c)}
                                        >
                                            {c}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {error && <div className="aiq-error">{error}</div>}
                        </div>

                        <Button size="lg" className="aiq-submit" onClick={handleSubmit} loading={submitting}>
                            {submitting ? '전송 중...' : '전송'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdmissionInquiryModal;
